import React from "react";

import { faPencil } from "@fortawesome/pro-solid-svg-icons";

import { Text } from "@performyard/py-components";

import { ChatErrorBanner } from "./ChatErrorBanner";
import { CompetencyPreviewCard, InferredLevelsLegend, hasInferredLevels } from "./CompetencyPreviewCard";
import { CreateAllCompetenciesButton } from "./CreateAllCompetenciesButton";
import GeneratedContentContainer from "./GeneratedContentContainer";

import { useCompetencyFrameworkView } from "../hooks/useCompetencyFrameworkView";

import { useAskPerformYardStore } from "../AskPerformYardStore";

export const CompetencyFrameworkCard: React.FC<{ messageId: string }> = ({ messageId }) => {
	const openCompetencyEditPanel = useAskPerformYardStore((state) => state.openCompetencyEditPanel);
	const isExpanded = useAskPerformYardStore((state) => state.isExpanded);
	const toggleExpand = useAskPerformYardStore((state) => state.toggleExpand);
	const { framework, editedFields, itemStatus, visible, pending, isDone, createOne, removeOne, isSaving, bulkSave } =
		useCompetencyFrameworkView();

	if (!framework || framework.messageId !== messageId) {
		return null;
	}
	const { status, errorMessage, itemErrors } = framework;
	const showInferredLegend = hasInferredLevels(visible, editedFields);

	const handleEdit = () => {
		if (!isExpanded) {
			toggleExpand();
		}
		openCompetencyEditPanel();
	};

	if (visible.length === 0) {
		return (
			<GeneratedContentContainer title="Competency Framework">
				<Text size="paragraph-3" color="neutral-7">
					All suggested competencies have been removed.
				</Text>
			</GeneratedContentContainer>
		);
	}

	return (
		<GeneratedContentContainer
			title="Competency Framework"
			headerAction={{
				label: "Edit",
				iconLeft: faPencil,
				onClick: handleEdit,
				disabled: isDone,
			}}
		>
			<Text size="caption" color="neutral-6" dangerouslySetClassName="-mt-2">
				{visible.length} {visible.length === 1 ? "competency" : "competencies"}
			</Text>
			{showInferredLegend && <InferredLevelsLegend />}
			<div className={`flex flex-col gap-2 pb-1 ${showInferredLegend ? "pt-1" : "pt-2"}`}>
				{visible.map((suggestion) => (
					<CompetencyPreviewCard
						key={suggestion.id}
						name={editedFields[suggestion.id]?.name ?? suggestion.name}
						description={editedFields[suggestion.id]?.description ?? suggestion.description}
						levels={editedFields[suggestion.id]?.levels ?? suggestion.levels}
						itemStatus={itemStatus(suggestion.id)}
						itemError={itemErrors[suggestion.id]}
						saving={isSaving}
						isCompanyWide={editedFields[suggestion.id]?.isCompanyWide ?? suggestion.isCompanyWide ?? false}
						onCreate={() => createOne(suggestion)}
						onRemove={() => removeOne(suggestion.id)}
					/>
				))}
			</div>

			{status === "error" && errorMessage && <ChatErrorBanner type="error" message={errorMessage} />}

			<div className="flex justify-end pt-1 pb-1">
				<CreateAllCompetenciesButton
					isDone={isDone}
					isSaving={isSaving}
					disabled={isSaving || pending.length === 0}
					onCreateAll={bulkSave}
					doneBehavior="view"
				/>
			</div>
		</GeneratedContentContainer>
	);
};
